/**
 * Resolves a router pathname into a stable usage descriptor for page-view
 * metrics: { feature, page, route_pattern }.
 *
 * Raw pathnames carry entity ids (/draft/abc123, /editor/draft/x/content/y)
 * which are high-cardinality and useless to aggregate. We instead emit
 * `<feature>.page_view` with a normalized route_pattern (ids collapsed to
 * :params) and a friendly page name, so dashboards group cleanly per feature.
 *
 * `feature` values intentionally match the action-event features elsewhere
 * (draft, editor, order, magazine, email-campaign, lead, plan, ...).
 */

// [path template, feature, page] — ordered most-specific first within a prefix.
// `:param` matches one segment; a trailing `?` makes the segment optional.
const ROUTES = [
  // Draft
  ['/welcome/draft/generate/:entity/:id/:flowPath', 'draft', 'draft-generate-welcome'],
  ['/draft/generate/:entity/:id', 'draft', 'draft-generate'],
  ['/draft/:draftId/checkout', 'draft', 'draft-checkout'],
  ['/draft/:draftId', 'draft', 'draft-detail'],

  // Editor
  ['/editor/draft/:draftId/content/:sectionId', 'editor', 'content-section'],
  ['/editor/draft/:draftId/content', 'editor', 'content'],
  ['/editor/draft/:draftId/manage', 'editor', 'manage-sections'],
  ['/editor/draft/:draftId', 'editor', 'cover'],
  ['/editor/order/:orderId', 'editor', 'order-editor'],

  // My Books
  ['/my-books/book-catalog', 'my-books', 'book-catalog'],
  ['/my-books/my-orders', 'my-books', 'my-orders'],
  ['/my-books/digital-books', 'my-books', 'digital-books'],
  ['/my-books/book-performance', 'my-books', 'book-performance'],
  ['/my-books/manage-book-version', 'my-books', 'manage-book-version'],
  ['/my-books/buy-credits', 'credits', 'buy-credits'],
  ['/my-books/checkout', 'order', 'checkout'],

  // Leads
  ['/leads/import', 'lead', 'import-leads'],
  ['/leads', 'lead', 'leads'],

  // Email campaigns & templates
  ['/dashboard/email-campaign/sender-email-configuration', 'email-campaign', 'sender-config'],
  ['/dashboard/email-campaign/templates/create/:templateBaseId?', 'email-template', 'create-template'],
  ['/dashboard/email-campaign/templates/edit/:id', 'email-template', 'edit-template'],
  ['/dashboard/email-campaign/templates', 'email-template', 'templates'],
  ['/dashboard/email-campaign/campaign-analytics', 'email-campaign', 'campaign-analytics'],
  ['/dashboard/email-campaign/campaigns/launch-campaign/:id/history', 'email-campaign', 'campaign-history'],
  ['/dashboard/email-campaign/campaigns/launch-campaign', 'email-campaign', 'launch-campaign'],
  ['/dashboard/email-campaign/campaigns/campaign-metrics', 'email-campaign', 'campaign-metrics'],
  ['/dashboard/email-campaign/campaigns/on-demand-emails/:id', 'email-campaign', 'on-demand-email-detail'],
  ['/dashboard/email-campaign/campaigns/on-demand-email', 'email-campaign', 'on-demand-email'],
  ['/dashboard/email-campaign/campaigns/:content/create', 'email-campaign', 'create-campaign'],
  ['/dashboard/email-campaign/campaigns/:id', 'email-campaign', 'campaign-detail'],
  ['/dashboard/email-history/:leadId/:origin', 'email-campaign', 'email-history'],

  // Referral marketing / magazine
  ['/admin/referral-marketing/magazine-editor/:month/:year/:customerId/:previewId?', 'magazine', 'magazine-editor-admin'],
  ['/referral-marketing/magazine-editor/:month/:year', 'magazine', 'magazine-editor'],
  ['/referral-marketing/generated-magazine', 'magazine', 'generated-magazine'],
  ['/referral-marketing/previous-magazines', 'magazine', 'previous-magazines'],
  ['/referral-marketing/magazine-recipients', 'magazine', 'magazine-recipients'],
  ['/referral-marketing/my-magazine', 'magazine', 'my-magazine'],

  // Plans & billing
  ['/plans/upgrade', 'plan', 'upgrade'],
  ['/plans/confirmation', 'plan', 'confirmation'],
  ['/plans', 'plan', 'plans'],
  ['/plans-and-billing/cancel', 'plan', 'cancel'],
  ['/plans-and-billing/upgrade', 'plan', 'upgrade'],
  ['/plans-and-billing', 'plan', 'plans-and-billing'],
  ['/billing', 'plan', 'billing'],
  ['/payments', 'plan', 'payments'],
  ['/profile/plans', 'plan', 'plan-settings'],

  // Profile
  ['/profile/password', 'profile', 'password'],
  ['/profile/page-profile', 'profile', 'page-profile'],
  ['/profile/shipping-address', 'profile', 'shipping-address'],
  ['/profile', 'profile', 'profile'],

  // Template builder
  ['/templates/home', 'template-builder', 'home'],
  ['/templates/create/:id', 'template-builder', 'create-project'],
  ['/templates/project/:id', 'template-builder', 'edit-project'],
  ['/dashboard/templates/my-projects', 'template-builder', 'my-projects'],
  ['/dashboard/templates/setup-defaults', 'template-builder', 'setup-defaults'],
  ['/dashboard/templates/:key', 'template-builder', 'template'],

  // Misc member-facing
  ['/marketing-hub', 'marketing-hub', 'marketing-hub'],
  ['/welcome-journey', 'home', 'welcome-journey'],
  ['/help-center', 'home', 'help-center'],
  ['/release-notes', 'home', 'release-notes'],
  ['/', 'home', 'home'],
];

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const compile = (template) => {
  if (template === '/') {
    return { re: /^\/?$/, pattern: '/' };
  }
  let re = '';
  const cleanSegs = [];
  template
    .split('/')
    .slice(1)
    .forEach((seg) => {
      if (seg.startsWith(':')) {
        const optional = seg.endsWith('?');
        cleanSegs.push(`:${seg.slice(1).replace(/\(.*\)/, '').replace(/\?$/, '')}`);
        re += optional ? '(?:/[^/]+)?' : '/[^/]+';
      } else {
        cleanSegs.push(seg);
        re += `/${escape(seg)}`;
      }
    });
  return { re: new RegExp(`^${re}/?$`), pattern: `/${cleanSegs.join('/')}` };
};

const COMPILED = ROUTES.map(([template, feature, page]) => {
  const { re, pattern } = compile(template);
  return { re, pattern, feature, page };
});

const looksLikeId = (s) =>
  /^\d+$/.test(s) ||
  /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s) ||
  /^[0-9a-fA-F]{16,}$/.test(s) ||
  (/\d/.test(s) && s.length >= 10);

// Anything not curated above: derive a feature from the first segment and a
// normalized pattern by collapsing id-like segments to :id.
const fallback = (segments) => {
  if (segments.length === 0) {
    return { feature: 'home', page: 'home', route_pattern: '/' };
  }
  const norm = segments.map((s) => (looksLikeId(s) ? ':id' : s));
  const feature = norm[0];
  const rest = norm.slice(1).filter((s) => s !== ':id');
  let page = feature;
  if (rest.length) {
    page = rest.join('-');
  } else if (norm.length > 1) {
    page = `${feature}-detail`;
  }
  return { feature, page, route_pattern: `/${norm.join('/')}` };
};

export const resolveRoute = (pathname) => {
  const path = (pathname || '/').split('?')[0].replace(/\/+$/, '') || '/';
  const match = COMPILED.find((r) => r.re.test(path));
  if (match) {
    return { feature: match.feature, page: match.page, route_pattern: match.pattern };
  }
  return fallback(path.split('/').filter(Boolean));
};

export default resolveRoute;
